import axios from "axios";
import { message } from "antd";
import apiEndpoints from "./endpoints";
import apiClient from "./exportClient";
import { useAuth } from "../store";
import SecureStorage from "../utils/secureStorage";
import { TokenUtils } from "../utils/tokenUtils";
import NetworkProtection from "../utils/networkProtection";
import { config } from "../../config";
import Logger from "../utils/logger";
import { captureApiError, addBreadcrumb } from "../utils/sentry";

const NoAccessTokenURL = [
  "user/login/",
  "user/forgot-password/",
  "user/reset-password/",
];

const noAlerts: string[] = [];

const handleAlerts = (error: any) => {
  // Capture API errors in Sentry (except for expected ones like 401)
  const status = error.response?.status;
  const url = error.config?.url || "unknown";
  const method = error.config?.method?.toUpperCase() || "UNKNOWN";

  // Only capture server errors (5xx) and unexpected client errors
  // Don't capture 401 (auth), 400 (validation), 403 (forbidden), 404 (not found)
  if (status >= 500 || (!status && error.request)) {
    captureApiError(error, url, method, status);
  }

  // Add breadcrumb for all API errors (for debugging context)
  addBreadcrumb({
    category: "api",
    message: `API ${method} ${url} failed with status ${
      status || "network error"
    }`,
    level: status >= 500 ? "error" : "warning",
    data: {
      url,
      method,
      status,
      hasResponse: !!error.response,
    },
  });

  if (!noAlerts.includes(window.location.pathname)) {
    if (error.response) {
      if (status === 401) {
        // Handle unauthorized access
      } else if (error.response.data.error) {
        // Remove sensitive data and show user-friendly message
        const errorMessage = config.isProduction()
          ? "An error occurred. Please try again."
          : error.response.data.error;
        message.error(errorMessage);
      } else {
        if (error.response.data.errors) {
          // Handle validation errors
          const errors = error.response.data.errors;
          if (errors.email) {
            message.error(
              Array.isArray(errors.email) ? errors.email[0] : errors.email
            );
          } else if (errors.non_field_errors) {
            message.error(
              Array.isArray(errors.non_field_errors)
                ? errors.non_field_errors[0]
                : errors.non_field_errors
            );
          } else if (errors.phone) {
            message.error(
              Array.isArray(errors.phone) ? errors.phone[0] : errors.phone
            );
          } else if (errors.terms_accepted) {
            message.error(
              Array.isArray(errors.terms_accepted)
                ? errors.terms_accepted[0]
                : errors.terms_accepted
            );
          } else {
            // Handle both string and object errors
            let errorMessage = "Please check your input and try again.";
            if (!config.isProduction()) {
              if (typeof errors === "string") {
                // errors is a string: "User Not Registerd with this email"
                errorMessage = errors;
              } else {
                // errors is an object: {"field": ["error"]}
                const firstError = Object.values(errors)[0];
                errorMessage = Array.isArray(firstError)
                  ? firstError[0]
                  : firstError || "Validation error";
              }
            }
            message.error(errorMessage);
          }
        }
      }
    } else {
      message.error("Network error. Please check your connection.");
    }
  }
};

const handleSuccess = (response: any) => {
  if (!noAlerts.includes(window.location.pathname)) {
    if (response.status === 200) {
      if (response.data.message) {
        message.success(response.data.message);
      }
    } else if (response.status === 201) {
      if (response.data.message) {
        message.success(response.data.message);
      }
    }
  }
};

let isRefreshing = false;

const refreshAccessToken = async () => {
  const isAuthenticated = useAuth.getState().isAuthenticated;
  const refreshToken = SecureStorage.getRefreshToken();

  isRefreshing = true;
  try {
    let newAccessToken: any;
    if (isAuthenticated && refreshToken) {
      // Check if refresh token is expired
      if (TokenUtils.isTokenExpired(refreshToken)) {
        Logger.debug("Refresh token expired, redirecting to login");
        useAuth.getState().clearAuth();
        window.location.href = "/login";
        return null;
      }

      const response = await apiClient
        .post(apiEndpoints.auth.refresh, {
          refresh: refreshToken,
        })
        .catch((err) => {
          Logger.error("refreshAccessTokenError: ", err);
          useAuth.getState().clearAuth();
          window.location.href = "/login";
        });

      newAccessToken = response?.data?.access;
      const newRefreshToken = response?.data?.refresh;

      if (newAccessToken) {
        // Update tokens in secure storage
        SecureStorage.setToken(newAccessToken);
        if (newRefreshToken) {
          SecureStorage.setRefreshToken(newRefreshToken);
        }

        // Update store
        useAuth.setState((state) => ({
          user: {
            ...state.user,
            token: newAccessToken,
            refresh: newRefreshToken || refreshToken,
          },
        }));
      }
    } else {
      useAuth.getState().clearAuth();
      window.location.href = "/login";
    }
    isRefreshing = false;
    return newAccessToken;
  } catch (error) {
    isRefreshing = false;
    Logger.error("Error refreshing access token:", error);
    useAuth.getState().clearAuth();
    throw error;
  }
};

axios.interceptors.request.use(
  async (requestConfig) => {
    // HTTPS enforcement for production - check baseURL, not relative URLs
    if (config.enforceHttps && config.baseUrl) {
      if (!config.baseUrl.startsWith("https://")) {
        Logger.security("HTTPS enforcement: Blocking non-HTTPS API base URL");
        throw new Error("HTTPS required for production");
      }
    }

    const accessToken = SecureStorage.getToken();

    let AccessTokenNeeded = true;
    NoAccessTokenURL.forEach((url) => {
      if (requestConfig.url === url) {
        AccessTokenNeeded = false;
      }
    });

    // Add network protection headers for obfuscation
    const noiseHeaders = NetworkProtection.addRequestNoise();
    Object.assign(requestConfig.headers, noiseHeaders);

    if (accessToken && AccessTokenNeeded) {
      // Check if token is expired before making request
      if (TokenUtils.isTokenExpired(accessToken)) {
        Logger.debug("Access token expired, attempting refresh");
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            requestConfig.headers.Authorization = `Bearer ${newToken}`;
          } else {
            // Refresh failed, redirect to login
            return Promise.reject(new Error("Authentication required"));
          }
        } catch (error) {
          Logger.error("Token refresh failed:", error);
          return Promise.reject(new Error("Authentication required"));
        }
      } else {
        requestConfig.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return requestConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => {
    handleSuccess(response);
    return response;
  },
  async (error) => {
    handleAlerts(error);
    if (error.response) {
      const status = error.response.status;
      const originalRequest = error.config;

      if (status === 401 && isRefreshing !== true) {
        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return axios(originalRequest); // Retry the original request with the new token
        } catch (refreshError) {
          // Handle refresh token error (e.g., redirect to login page)
          Logger.error("Error refreshing access token:", refreshError);
        }
      }
    } else if (error.request) {
      Logger.debug("Request Error - Network issue");
    } else {
      Logger.debug("Error Message:", error.message);
    }

    return Promise.reject(error);
  }
);

export default axios;
